import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { ArrowUpRight, ChevronLeft, ChevronRight, Loader2, Search } from "lucide-react";
import { api } from "../lib/api";
import { BLOG_EDITORIAL_CATEGORIES as editorialTopics } from "../lib/blogCategories";
import { useAuth } from "../lib/auth";
import { BlogStoryCard, BlogShareDialog, type BlogCard } from "../components/BlogStoryCard";
import "./Blogs.css";
import BlogsHero from "./BlogsHero";
import { emptyBlogPlacements, splitBlogFeed, type BlogPlacements } from "../lib/blogPlacements";

const hiddenTopics = new Set(["Professional development", "Insights", "Career"]);
const TOPIC_SCROLL_STEP = 220;
// Keep the copy cutoff aligned with Blogs.css without replacing the focused input.
const MOBILE_BLOG_QUERY = "(max-width: 640px)";

export default function Blogs() {
  const { user, loading } = useAuth();
  const [posts, setPosts] = useState<BlogCard[] | null>(null);
  const [placements, setPlacements] = useState<BlogPlacements>(emptyBlogPlacements);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [topic, setTopic] = useState("All stories");
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(8);
  const [sharing, setSharing] = useState<BlogCard | null>(null);
  const topicsRef = useRef<HTMLElement>(null);
  const [topicScroll, setTopicScroll] = useState({ previous: false, next: false });
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_BLOG_QUERY).matches);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_BLOG_QUERY);
    const updateMobile = () => setIsMobile(media.matches);
    media.addEventListener("change", updateMobile);
    updateMobile();
    return () => media.removeEventListener("change", updateMobile);
  }, []);

  useEffect(() => {
    if (loading) return;
    let alive = true;
    setError(null);
    api.listBlog().then(response => { if (alive) { setPosts(response.posts); setPlacements(response.placements ?? emptyBlogPlacements); } })
      .catch(() => { if (alive) setError("Could not load stories. Please try again."); });
    return () => { alive = false; };
  }, [attempt, loading, user?.id]);

  useEffect(() => {
    const rail = topicsRef.current;
    if (!rail) return;
    const updateScroll = () => {
      // Fractional browser scroll offsets need a small tolerance at either end.
      const previous = rail.scrollLeft > 2;
      const next = rail.scrollWidth - rail.clientWidth - rail.scrollLeft > 2;
      setTopicScroll(current => current.previous === previous && current.next === next
        ? current : { previous, next });
    };
    updateScroll();
    const observer = new ResizeObserver(updateScroll);
    observer.observe(rail);
    // Fonts and saved categories can change overflow without changing the rail width.
    for (const button of rail.children) observer.observe(button);
    rail.addEventListener("scroll", updateScroll, { passive: true });
    return () => {
      observer.disconnect();
      rail.removeEventListener("scroll", updateScroll);
    };
  }, [posts]);

  function scrollTopics(direction: -1 | 1) {
    topicsRef.current?.scrollBy({
      left: direction * TOPIC_SCROLL_STEP,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth",
    });
  }

  const topics = ["All stories", ...[...new Set([...editorialTopics, ...(posts ?? []).flatMap(post => post.category ? [post.category] : [])])].filter(name => !hiddenTopics.has(name))];
  const matching = (posts ?? []).filter(post => (topic === "All stories" || post.category === topic)
    && [post.title, post.excerpt, post.category, post.authorName, ...post.tags].join(" ").toLowerCase().includes(query.trim().toLowerCase()));
  const { featured, pinned, remaining } = splitBlogFeed(matching, placements);

  const searchForm = <form className="bp-search" onSubmit={event => { event.preventDefault(); document.getElementById("blog-stories")?.scrollIntoView({ block: "start" }); }}>
        <Search size={18} aria-hidden="true" />
        <input value={query} onChange={event => { setQuery(event.target.value); setLimit(8); }} placeholder={isMobile ? "Search stories…" : "Discover your next idea…"} aria-label="Search stories" type="search" />
        <button type="submit" aria-label="Search stories"><Search size={26} aria-hidden="true" /></button>
      </form>;
  const topicNavigation = <div className="bp-topics-wrap">
        <nav ref={topicsRef} id="blog-topics" className="bp-topics" aria-label="Story topics" onFocus={event => {
          // Edge arrows must not cover the topic receiving keyboard focus.
          if (event.currentTarget.scrollWidth > event.currentTarget.clientWidth && event.target instanceof HTMLElement) {
            event.target.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "instant" });
          }
        }}>{topics.map(name => <button key={name} aria-pressed={topic === name} onClick={() => { setTopic(name); setLimit(8); }}>{name}</button>)}</nav>
        <div className="bp-topic-scroll bp-topic-scroll-previous"><button type="button" aria-label="See previous topics" aria-controls="blog-topics" disabled={!topicScroll.previous} onClick={() => scrollTopics(-1)}><ChevronLeft size={20} aria-hidden="true" /></button></div>
        <div className="bp-topic-scroll bp-topic-scroll-next"><button type="button" aria-label="See more topics" aria-controls="blog-topics" disabled={!topicScroll.next} onClick={() => scrollTopics(1)}><ChevronRight size={20} aria-hidden="true" /></button></div>
      </div>;

  return <div className="blogs-page">
    <BlogsHero search={searchForm} topics={topicNavigation} />
    <div className="bp-content" id="blog-stories">
      {error ? <div className="bp-empty" role="alert"><p>{error}</p><button onClick={() => setAttempt(value => value + 1)}>Try again</button></div>
        : !posts ? <p className="bp-loading" role="status"><Loader2 className="animate-spin" size={18} />Loading stories…</p>
        : matching.length ? <>
          {featured && <section className="bp-editorial-section" aria-labelledby="blog-hot-heading">
            <div className="bp-section-heading"><h2 id="blog-hot-heading">What’s hot</h2></div>
            <Link href={`/blogs/${encodeURIComponent(featured.slug)}`} className="bp-feature">
              <img src={featured.coverImage || "/hero/professional-services.jpg"} alt="" />
              <div><span className="bp-eyebrow">{featured.category}</span><h2>{featured.title}</h2><p>{featured.excerpt}</p><span>Read the story <ArrowUpRight size={20} aria-hidden="true" /></span></div>
            </Link>
          </section>}
          {pinned.length > 0 && <section className="bp-editorial-section" aria-labelledby="blog-latest-heading">
            <div className="bp-section-heading"><h2 id="blog-latest-heading">Latest</h2><span>{pinned.length} {pinned.length === 1 ? "story" : "stories"}</span></div>
            <div className="bp-grid">{pinned.map(post => <BlogStoryCard key={post.id} post={post} onShare={() => setSharing(post)} />)}</div>
          </section>}
          {remaining.length > 0 && <section className="bp-editorial-section" aria-labelledby="blog-stories-heading">
            <div className="bp-section-heading"><h2 id="blog-stories-heading">{topic === "All stories" ? "More stories" : topic}</h2><span>{remaining.length} {remaining.length === 1 ? "story" : "stories"}</span></div>
            <div className="bp-grid">{remaining.slice(0, limit).map(post => <BlogStoryCard key={post.id} post={post} onShare={() => setSharing(post)} />)}</div>
            {remaining.length > limit && <button className="bp-more" onClick={() => setLimit(value => value + 4)}>Discover more <ArrowUpRight size={16} aria-hidden="true" /></button>}
          </section>}
        </> : <div className="bp-empty"><h3>{posts.length ? "No stories found" : "New stories are on the way"}</h3>
          <p>{posts.length ? "Try another topic or search phrase." : "Check back soon for guides, insights and updates from KontrataPH."}</p>
          {(query || topic !== "All stories") && <button onClick={() => { setTopic("All stories"); setQuery(""); setLimit(8); }}>Show all stories</button>}
        </div>}
    </div>
    {sharing && <BlogShareDialog post={sharing} onClose={() => setSharing(null)} />}
  </div>;
}
